import React, { Suspense, useEffect, useMemo } from 'react';
import { useLocation, useRoutes } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CContainer } from '@coreui/react-pro';

import { tryParseJSON } from '../../utils';
import { LOCAL_ORG_USER, MY_TRAINING, STATUS, SUBSCRIBER_PATH } from '../../constants';

import BreadCrumbRouter from '../../components/General/BreadCrumbRouter';
import CenterSpinner from '../../components/General/Loadings/CenterSpinner';

import { organizationRoutes, subscriberRoutes } from '../../routes';
import ExpirationDate from './ExpirationDate';
import { fetchCoursesRequest } from '../../components/CMS/Subscriber/MyLeaning/Course/actions';
import { fetchSubscriberProductsRequest } from '../../components/CMS/Subscriber/Products/actions';

const SubscriberContent = () => {
     const dispatch = useDispatch();
     const currentLocation = useLocation().pathname;

     const courses = useSelector((state) => state.subscriber.courses);
     const products = useSelector((state) => state.subscriber.products.list);

     const paramArray = currentLocation.split('/').filter((locationPath) => locationPath);
     const activeOrganizationOwner = tryParseJSON(localStorage.getItem(LOCAL_ORG_USER));

     const fetchProducts = () => {
          if (!products.length) {
               dispatch(fetchSubscriberProductsRequest());
          }
     };
     useEffect(() => {
          fetchProducts();
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     const routes = activeOrganizationOwner ? organizationRoutes : subscriberRoutes;
     const element = useRoutes(routes);

     let checkBgGrey = false;
     let courseDetail;
     let productSlug = '';

     if (paramArray.length) {
          if (paramArray[paramArray.length - 1] === 'lessons') {
               // Lesson
               const courseId = paramArray[paramArray.length - 2];
               if (courseId) {
                    courseDetail = courses?.find((course) => course.id === courseId);
               }
          } else if (paramArray[paramArray.length - 2] === 'event') {
               // Event
               const eventId = paramArray[paramArray.length - 1];

               if (eventId) {
                    courseDetail = courses?.find((course) => course.eventId === eventId);
               }
          } else if (['test'].includes(paramArray[paramArray.length - 2]) || ['survey'].includes(paramArray[paramArray.length - 1])) {
               checkBgGrey = true;
          }

          if (paramArray[paramArray.length - 2] === MY_TRAINING) {
               productSlug = paramArray[paramArray.length - 1];
          }
     }

     const fetchData = () => {
          if (!courses) {
               dispatch(fetchCoursesRequest());
          }
     };

     useEffect(fetchData, []); // eslint-disable-line react-hooks/exhaustive-deps

     const product = products.find((product) => product.id === courseDetail?.productId);
     const lessons =
          product?.lessons.filter((item) => item.status === STATUS.ENABLE).sort((firstItem, secondItem) => firstItem.order - secondItem.order) ?? [];

     const expireDay = (product?.accessExpirationDays > 0 && product?.expireUserAccess && courseDetail?.seat?.expiredAt) || null;

     const productsExpiredInCourse = useMemo(() => {
          if (!productSlug) return [];

          const productsInCourse = [];

          const product = products.find((productItem) => productItem.slug === productSlug);
          if (!product) return [];

          const courseHasExpired = courses?.find(
               (course) =>
                    course.productId === product.id && course.seat?.expiredAt && new Date().getTime() > new Date(course.seat?.expiredAt).getTime()
          );

          if (courseHasExpired) {
               productsInCourse.push({
                    id: product.id,
                    publishName: product.publishName,
               });
          }

          // Check more related products
          if (!product.relatedProductProductId) return productsInCourse;

          const relatedProduct = products.find((productItem) => productItem.id === product.relatedProductProductId);
          if (!relatedProduct) return productsInCourse;

          const relatedCourseHasExpired = courses?.find(
               (course) =>
                    course.productId === relatedProduct.id &&
                    course.seat?.expiredAt &&
                    new Date().getTime() > new Date(course.seat?.expiredAt).getTime()
          );

          if (relatedCourseHasExpired) {
               productsInCourse.push({
                    id: relatedProduct.id,
                    publishName: relatedProduct.publishName,
               });
          }

          return productsInCourse;
     }, [productSlug, products, courses]);

     const checkShowBreadCrumb = currentLocation !== SUBSCRIBER_PATH.DASHBOARD && !currentLocation.includes('drm');

     return (
          <main>
               <section className={`section-breadcrumb ${checkBgGrey ? ' bg-grey' : ''} ${!checkShowBreadCrumb ? 'mb-3' : ''}`}>
                    {checkShowBreadCrumb && (
                         <CContainer>
                              <div className="container-content">
                                   <BreadCrumbRouter routes={subscriberRoutes} />

                                   {productsExpiredInCourse.length > 0 && (
                                        <div className="course-access-expired">
                                             {productsExpiredInCourse.map((productInCourse) => {
                                                  return (
                                                       <div key={productInCourse.id} className="course-access-expired-item">
                                                            {productInCourse.publishName + ':'}
                                                            <span className="course-access-expired-highlight">Your Access Expired</span>
                                                       </div>
                                                  );
                                             })}
                                        </div>
                                   )}

                                   <div className="notify-course-wrap">
                                        {expireDay && <ExpirationDate expireDay={expireDay} />}

                                        {product?.courseLengthDescription?.trim() && (
                                             <div className="estimate-course-complete">
                                                  Time required to complete {lessons.length !== 1 ? 'these Lessons: ' : 'this Lesson: '}
                                                  {product.courseLengthDescription}
                                             </div>
                                        )}
                                   </div>
                              </div>
                         </CContainer>
                    )}
               </section>

               <Suspense fallback={<CenterSpinner classNameWrap="page-loading-top" />}>{element}</Suspense>
          </main>
     );
};

export default SubscriberContent;
